/**
 * Slot-based inventory with per-kind stacking.
 *
 * The slot array identity is stable for the life of the system: `game.ts`
 * captures `survival.inventory` once when it builds the frame context, so this
 * mutates in place and never reassigns.
 */

import type { InventorySlot, ItemKind } from '../core/types';
import { itemDef } from './items';

export class Inventory {
  readonly slots: InventorySlot[] = [];

  constructor(private readonly capacity: number) {}

  count(kind: ItemKind): number {
    let n = 0;
    for (const s of this.slots) if (s.kind === kind) n += s.count;
    return n;
  }

  get full(): boolean {
    return this.slots.length >= this.capacity;
  }

  /** How many more of `kind` fit, across part-filled stacks and empty slots. */
  room(kind: ItemKind): number {
    const stack = itemDef(kind).stackSize;
    let room = (this.capacity - this.slots.length) * stack;
    for (const s of this.slots) if (s.kind === kind) room += stack - s.count;
    return room;
  }

  /**
   * All-or-nothing. A partial pickup would leave the world item in a half
   * state with no way to show it, so a full bag simply refuses.
   */
  add(kind: ItemKind, amount: number): boolean {
    if (amount <= 0 || this.room(kind) < amount) return false;
    const stack = itemDef(kind).stackSize;
    let left = amount;
    for (const s of this.slots) {
      if (left <= 0) break;
      if (s.kind !== kind || s.count >= stack) continue;
      const take = Math.min(stack - s.count, left);
      s.count += take;
      left -= take;
    }
    while (left > 0) {
      const take = Math.min(stack, left);
      this.slots.push({ kind, count: take });
      left -= take;
    }
    return true;
  }

  /** Removes from the smallest stacks first so part-filled slots get tidied up. */
  remove(kind: ItemKind, amount = 1): boolean {
    if (this.count(kind) < amount) return false;
    let left = amount;
    for (let i = this.slots.length - 1; i >= 0 && left > 0; i--) {
      const s = this.slots[i]!;
      if (s.kind !== kind) continue;
      const take = Math.min(s.count, left);
      s.count -= take;
      left -= take;
      if (s.count <= 0) this.slots.splice(i, 1);
    }
    return true;
  }

  kindAt(index: number): ItemKind | null {
    return this.slots[index]?.kind ?? null;
  }

  clear(): void {
    this.slots.length = 0;
  }
}
